// Moving project payloads out of localStorage — the plan, as a pure function (rule 10).
//
// **Why the payloads moved (08.10).** Every project's scenario, timeline, narration timings,
// review and publish package lived in localStorage, which Chrome caps at ~5M UTF-16 code units
// (about 10 MB) for the whole origin. With fifteen projects, several of them megabytes each, the
// cap was reached and a compile could no longer be saved («Компиляция собрана, но НЕ
// сохранилась»). IndexedDB's quota is a share of the disk, not a fixed 10 MB, so the payloads now
// live there (`payloadStore.ts`), in their own database `storydress-projects`.
//
// This file decides, for every payload key still found in localStorage, what to do with it. The
// executor (`payloadStore.ts#hydrate`) does the I/O; nothing here touches storage, so every rule
// below is checked in `tests/payload-migration.test.mjs` without a browser.
//
// **The invariant the plan keeps: a localStorage copy is only replaced once IndexedDB holds the
// same string, read back and compared.** A step that fails at any point leaves localStorage
// exactly as it was, and the app reads that copy as before.
//
// Where a payload key in localStorage can come from, once a browser has migrated:
//  - **never migrated** — the first load of this build (IndexedDB holds nothing for it);
//  - **the unload journal** — a write still in flight when the page went away is copied into
//    localStorage at `pagehide` and listed in `JOURNAL_KEY` with its timestamp, because a page
//    being torn down cannot be trusted to finish an IndexedDB transaction;
//  - **the write fallback** — an IndexedDB write that failed is retried into localStorage, also
//    journaled;
//  - **an older build** — a tab still running yesterday's code saves where it always did.
//
// A journaled entry carries a timestamp, so it is compared with the stored record's: the newer
// one wins, and the loser is an older state of the same key, not lost work. An entry with no
// journal line (never migrated, or an older build) has no timestamp to compare: it wins, because
// a write that happened after the migration is newer by construction — and the record it
// replaces is kept in the `superseded` store, so a wrong guess costs nothing.

/** The five slots that belong to one project and can grow to megabytes. */
export const PAYLOAD_SUFFIXES = ['timeline', 'scenario', 'audio', 'review', 'publish'] as const;

const PAYLOAD_KEY_RE = /^storydress:project:([^:]+):(timeline|scenario|audio|review|publish)$/;

/** `storydress:project:<id>:<one of the five>` — the keys that live in IndexedDB now. */
export function isPayloadKey(key: string): boolean {
  return PAYLOAD_KEY_RE.test(key);
}

/** The project id a payload key belongs to, or null for any other key. */
export function payloadKeyProject(key: string): string | null {
  return PAYLOAD_KEY_RE.exec(key)?.[1] ?? null;
}

/**
 * What a migrated key leaves behind in localStorage.
 *
 * Not nothing, on purpose: a tab still running the previous build reads payloads from
 * localStorage, and to that code an absent payload means "this project is empty". Its media sweep
 * (`mediaOwnership.ts`) would then find no project referencing anybody's footage and delete it
 * all. A payload with a version no loader accepts is "unreadable" instead, and an unreadable
 * project **cancels** the sweep — the safety valve that build already has. Loaders return null for
 * it, so an old tab shows the project as empty and writes nothing over it.
 */
export const PAYLOAD_STUB = '{"version":0,"storedIn":"indexedDB"}';

export function isPayloadStub(raw: string | null | undefined): boolean {
  return raw === PAYLOAD_STUB;
}

/** localStorage key of the unload journal: `{ [payloadKey]: { at, deleted? } }`. */
export const JOURNAL_KEY = 'storydress:payload-journal';

export interface JournalEntry {
  /** When the write was made (ms since epoch) — the same clock the stored record carries. */
  at: number;
  /** The write was a delete (a project removed while its tombstone was still in flight). */
  deleted?: boolean;
}

export type Journal = Record<string, JournalEntry>;

/** One record of the IndexedDB store: the payload string, verbatim, and when it was written. */
export interface StoredPayload {
  v: string;
  at: number;
}

export type MigrationStep =
  /**
   * Write `value` to IndexedDB, read it back, compare; only on an exact match replace the
   * localStorage copy with the stub. `archive` is the record being replaced, kept in
   * `superseded` first — present only when the two disagree and neither has a timestamp story.
   */
  | { kind: 'move'; key: string; value: string; at: number; archive?: StoredPayload }
  /** IndexedDB already holds exactly this string: just stub the localStorage copy. */
  | { kind: 'already-stored'; key: string }
  /** The journal copy is older than the stored record: the record stands, the copy is stubbed. */
  | { kind: 'stored-is-newer'; key: string }
  /** A delete that was still in flight at unload: remove the stored record. */
  | { kind: 'delete'; key: string }
  /** A journal line with nothing left to do (its copy never landed, or the record is newer). */
  | { kind: 'forget-journal'; key: string };

export interface MigrationInput {
  /** Payload keys found in localStorage, with their values; stubs already filtered out. */
  local: { key: string; value: string }[];
  journal: Journal;
  /** What IndexedDB holds now, by key. */
  stored: ReadonlyMap<string, StoredPayload>;
  /** Timestamp to give a copy that has none of its own. */
  now: number;
}

/**
 * What to do with each payload key left in localStorage, in the order to do it.
 *
 * Order: sorted by key, so a project's slots travel together and a run is reproducible. Order
 * does not affect safety — every step is independent and verify-before-stub — so a run that stops
 * half-way leaves some keys moved and the rest exactly where they were, and the next load picks up
 * the rest.
 *
 * On a read-back mismatch the executor keeps localStorage as it was and puts the previous record
 * back (or deletes the one it wrote); the app reads the localStorage copy, as it did before.
 */
export function planPayloadMigration(input: MigrationInput): MigrationStep[] {
  const steps: MigrationStep[] = [];
  const localKeys = new Set<string>();
  const local = [...input.local].filter((e) => isPayloadKey(e.key) && !isPayloadStub(e.value));
  local.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

  for (const { key, value } of local) {
    localKeys.add(key);
    const stored = input.stored.get(key);
    const journal = input.journal[key];
    const journaled = journal && !journal.deleted ? journal : undefined;
    if (stored && stored.v === value) {
      steps.push({ kind: 'already-stored', key });
      continue;
    }
    if (journaled) {
      if (stored && stored.at > journaled.at) steps.push({ kind: 'stored-is-newer', key });
      else steps.push({ kind: 'move', key, value, at: journaled.at });
      continue;
    }
    // No timestamp: never migrated, or written by an older build after the migration. Either way
    // it is the newest thing anyone wrote under this key — and a record it replaces is archived.
    steps.push(stored ? { kind: 'move', key, value, at: input.now, archive: stored } : { kind: 'move', key, value, at: input.now });
  }

  // Journal lines with no copy in localStorage: deletes still in flight at unload, or a copy that
  // never landed (the journal line is written after its copy, so this is rare).
  for (const key of Object.keys(input.journal).sort()) {
    if (localKeys.has(key)) continue; // handled above, with its copy
    const entry = input.journal[key];
    const stored = input.stored.get(key);
    if (isPayloadKey(key) && entry?.deleted && stored && stored.at <= entry.at) steps.push({ kind: 'delete', key });
    else steps.push({ kind: 'forget-journal', key });
  }
  return steps;
}

/** How many archived copies of one key the `superseded` store keeps (newest first). */
export const SUPERSEDED_KEEP = 3;

/** Of the archive ids for one key (`<key>@<at>`), the ones to delete — all but the newest few. */
export function supersededToPrune(ids: string[], key: string, keep = SUPERSEDED_KEEP): string[] {
  const prefix = `${key}@`;
  const mine = ids
    .filter((id) => id.startsWith(prefix) && /^\d+$/.test(id.slice(prefix.length)))
    .sort((a, b) => Number(b.slice(prefix.length)) - Number(a.slice(prefix.length)));
  return mine.slice(Math.max(0, keep));
}
